/**
 * Equipment Checks Service
 *
 * Data access layer for the Equipment Check feature.
 * See docs/EQUIPMENT_CHECK_SPEC.md for the model.
 */

import { supabase } from '../lib/supabase';
import { compressImage } from '../lib/storage';
import { fetchAllPages, fetchAllPagesByChunks, dedupeById } from '../lib/fetchAllPages';

// `equipment_checks` table and the new RPCs (swap_booking_item_equipment,
// ensure_equipment_checks_for_overdue_bookings) are not in the generated
// Supabase types yet — regenerate `database.types.ts` after applying the
// 20260514 migration. Until then we narrow to `any` at the call sites.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sb: any = supabase;

// Fetch translated product_type names for a set of product_type_ids. Returns
// a Map from product_type_id to the translated product_name. When language
// equals defaultLanguage (or any param is missing) returns an empty map so
// callers fall back to the base column.
async function fetchProductTypeNameTranslations(
  productTypeIds: string[],
  language: string | null | undefined,
  defaultLanguage: string | null | undefined,
): Promise<Map<string, string>> {
  if (!language || !defaultLanguage || language === defaultLanguage || productTypeIds.length === 0) {
    return new Map();
  }
  const data = await fetchAllPagesByChunks<any, string>(
    productTypeIds,
    (ids, from, to) =>
      supabase
        .from('product_type_translations')
        .select('product_type_id, product_name')
        .in('product_type_id', ids)
        .eq('language_code', language)
        .order('product_type_id', { ascending: true })
        .range(from, to)
  );
  const map = new Map<string, string>();
  for (const r of data as any[]) {
    if (r.product_name) map.set(r.product_type_id, r.product_name);
  }
  return map;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EquipmentCheckStatus = 'pending' | 'checked' | 'obsolete';

export type CustomerReturnBadge =
  | { kind: 'item_ok' }
  | { kind: 'item_issue' }
  | { kind: 'item_damaged' }
  | { kind: 'item_missing' }
  | { kind: 'booking_ok' }
  | { kind: 'booking_issues' }
  | { kind: 'none' };

export interface EquipmentCheckRow {
  id: string;
  merchant_id: string;
  location_id: string;
  booking_id: string;
  inventory_item_id: string;
  status: EquipmentCheckStatus;
  checked_by: string | null;
  checked_by_name: string | null;
  checked_at: string | null;
  note: string | null;
  photo_paths: string[];
  issue_id: string | null;
  obsolete_at: string | null;
  obsolete_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface EquipmentCheckListItem extends EquipmentCheckRow {
  // Booking
  order_id: string;
  customer_name: string;
  booking_start_date: string;
  booking_end_date: string;
  booking_returned_at: string | null;
  booking_state: string;
  // Item
  item_number: string;
  combination_code: string | null;
  product_type_id: string | null;
  product_name: string | null;
  // Customer return context (derived)
  customer_return: CustomerReturnBadge;
  customer_return_photos: number;
  // Next booking for this inventory_item
  next_booking_id: string | null;
  next_booking_start: string | null;
  next_booking_end: string | null;
  next_booking_customer: string | null;
  // All open issues on this inventory_item (any source, not just this check)
  open_issues: Array<{ id: string; title: string; severity: string; status: string }>;
  // Issue raised during this specific check (if any) — already covered by open_issues
  // but kept for back-compat; same record will appear in open_issues too.
  issue_title: string | null;
  issue_severity: string | null;
  // Flagged after check (customer reported issue after row was marked checked)
  customer_flagged_after_check: boolean;
}

export interface UpcomingHandoverTrackedItem {
  inventory_item_id: string;
  item_number: string;
  combination_code: string | null;
  product_name: string | null;
  last_check_status: 'checked' | 'pending' | 'none' | 'currently_out';
  // Set when last_check_status === 'currently_out': the end_date of the
  // active booking the bike is currently on.
  currently_out_end_date: string | null;
  attention: 'red' | 'yellow' | null;
  attention_reason: string | null;
}

export interface UpcomingHandoverAddon {
  booking_item_id: string;
  item_name: string;
  quantity: number;
}

export interface UpcomingHandover {
  booking_id: string;
  order_id: string;
  customer_name: string;
  start_date: string;
  tracked: UpcomingHandoverTrackedItem[];
  addons: UpcomingHandoverAddon[];
}

// ---------------------------------------------------------------------------
// Queue query
// ---------------------------------------------------------------------------

interface GetQueueArgs {
  merchantId: string;
  locationId: string | null; // null = all merchant locations (admin/manager scope)
  status: 'pending' | 'checked' | 'all';
  language?: string;
  defaultLanguage?: string;
  // Cap for the "completed" half of the result — applies to:
  //   - status='checked': sort by checked_at DESC, limit completedLimit
  //   - status='all': priority-grouped pending stays unbounded; checked +
  //                   obsolete are sorted by completion time DESC, limit.
  // Returned `has_more_completed` lets the UI render a "Show more" footer.
  completedLimit?: number;
}

interface GetQueueResult {
  data: EquipmentCheckListItem[];
  has_more_completed: boolean;
  error: string | null;
}

export async function getEquipmentChecks(
  args: GetQueueArgs
): Promise<GetQueueResult> {
  const completedLimit = args.completedLimit ?? 50;
  const selectShape =
    `*,
     bookings (
       id, order_id, customer_firstname, customer_lastname,
       start_date, end_date, returned_at, booking_state
     ),
     inventory_items (
       id, item_number, combination_code, product_type_id,
       product_types!inventory_items_product_type_id_fkey ( product_name )
     ),
     checked_by_profile:profiles!equipment_checks_checked_by_fkey ( full_name )`;

  function baseQuery() {
    let q = sb.from('equipment_checks').select(selectShape).eq('merchant_id', args.merchantId);
    if (args.locationId) q = q.eq('location_id', args.locationId);
    return q;
  }

  // The pending queue is unbounded by nature — it is however many bikes are
  // waiting to be checked — so it is paged rather than fetched in one request
  // that would silently stop at the row cap. The completed side already bounds
  // itself with an explicit .limit(), so it needs no paging.
  async function fetchAllPending(): Promise<any[]> {
    return dedupeById(
      await fetchAllPages<any>((from, to) =>
        baseQuery()
          .eq('status', 'pending')
          .order('id', { ascending: true })
          .range(from, to)
      )
    );
  }

  // For 'checked' we sort + limit on the query. For 'all' we run two queries
  // (pending paged in full, completed sorted+limited) and merge. For 'pending'
  // it's the paged path on its own.
  let rawRows: any[] = [];
  let has_more_completed = false;

  if (args.status === 'pending') {
    try {
      rawRows = await fetchAllPending();
    } catch (error: any) {
      return { data: [], has_more_completed: false, error: error?.message || String(error) };
    }
  } else if (args.status === 'checked') {
    // Fetch one extra row to detect "more available".
    const { data, error } = await baseQuery()
      .eq('status', 'checked')
      .order('checked_at', { ascending: false, nullsFirst: false })
      .limit(completedLimit + 1);
    if (error) return { data: [], has_more_completed: false, error: error.message };
    if ((data || []).length > completedLimit) {
      has_more_completed = true;
      rawRows = (data as any[]).slice(0, completedLimit);
    } else {
      rawRows = data || [];
    }
  } else {
    // 'all' — pending (paged, unbounded) + completed (checked/obsolete, sorted, limited)
    let pendingRows: any[];
    let completedRes: { data: any[] | null; error: { message: string } | null };
    try {
      [pendingRows, completedRes] = await Promise.all([
        fetchAllPending(),
        baseQuery()
          .in('status', ['checked', 'obsolete'])
          // checked_at is null for obsolete rows; obsolete_at is null for checked.
          // Order by the COALESCE-equivalent via two sort keys.
          .order('checked_at', { ascending: false, nullsFirst: false })
          .order('obsolete_at', { ascending: false, nullsFirst: false })
          .limit(completedLimit + 1),
      ]);
    } catch (error: any) {
      return { data: [], has_more_completed: false, error: error?.message || String(error) };
    }
    if (completedRes.error) return { data: [], has_more_completed: false, error: completedRes.error.message };
    const completedRows = completedRes.data || [];
    if (completedRows.length > completedLimit) {
      has_more_completed = true;
      rawRows = [...pendingRows, ...completedRows.slice(0, completedLimit)];
    } else {
      rawRows = [...pendingRows, ...completedRows];
    }
  }

  if (rawRows.length === 0) return { data: [], has_more_completed, error: null };

  const bookingIds: string[] = Array.from(new Set((rawRows as any[]).map((r) => r.booking_id as string)));
  const inventoryItemIds: string[] = Array.from(new Set((rawRows as any[]).map((r) => r.inventory_item_id as string)));
  const issueIds: string[] = (rawRows as any[]).map((r) => r.issue_id).filter(Boolean) as string[];

  // Steps 2-6 fan out from the rows above. Each `.in(...)` list is now as long
  // as the queue itself, so every one is chunked (to keep the URL short) and
  // paged (a single chunk can still match more rows than the cap — photos
  // especially, since one return carries many).
  // Step 2: booking_returns for these bookings (overall_status + id for photo lookup)
  //
  // A booking can carry several return versions — a re-check writes a new row
  // rather than editing the old one — and the queue must describe the current
  // one. Sorting by `version` makes the last row written per booking the newest
  // (the map below is last-wins), with `id` as a tie-breaker so legacy rows
  // whose version is null still resolve deterministically. Ordering on `version`
  // rather than `is_latest` means a booking is never dropped from the queue if
  // that flag was never populated for it.
  const returnsData = await fetchAllPagesByChunks<any, string>(
    bookingIds,
    (ids, from, to) =>
      supabase
        .from('booking_returns')
        .select('id, booking_id, version, overall_status, review_status, reviewed_at, created_at')
        .in('booking_id', ids)
        .order('version', { ascending: true, nullsFirst: true })
        .order('id', { ascending: true })
        .range(from, to)
  );

  const returnsByBooking = new Map<string, any>();
  for (const r of returnsData) {
    returnsByBooking.set(r.booking_id, r); // last wins = highest version
  }

  // Only the selected (newest) return of each booking feeds the item-status and
  // photo lookups below, so a superseded version cannot shadow the current one
  // — which would show an item's old condition, or count photos against a
  // return the queue is not displaying.
  const latestReturns = Array.from(returnsByBooking.values());
  const returnById = new Map<string, any>(latestReturns.map((r) => [r.id, r]));
  const returnIds: string[] = latestReturns.map((r) => r.id);

  // Step 3: booking_item_returns (item-level statuses)
  //
  // Filtered on booking_return_id only. The old query also constrained
  // inventory_item_id, but both lists are unbounded now and two long `.in()`
  // filters cannot be chunked independently in one request. The extra rows are
  // harmless: the key map below is only ever read for pairs that appear in
  // rawRows, so item returns for other items in the same booking are ignored.
  const itemReturnsData = await fetchAllPagesByChunks<any, string>(
    returnIds,
    (ids, from, to) =>
      supabase
        .from('booking_item_returns')
        .select('id, booking_return_id, inventory_item_id, item_status')
        .in('booking_return_id', ids)
        .order('id', { ascending: true })
        .range(from, to)
  );

  const itemReturnByKey = new Map<string, any>(); // key = `${booking_id}|${inventory_item_id}`
  const itemReturnIds: string[] = [];
  for (const ir of itemReturnsData) {
    const br = returnById.get(ir.booking_return_id);
    if (!br) continue;
    itemReturnByKey.set(`${br.booking_id}|${ir.inventory_item_id}`, ir);
    itemReturnIds.push(ir.id);
  }

  // Step 4: photo counts per booking_return (booking-level + item-level for THIS item)
  const photoCountByKey = new Map<string, number>(); // key = `${booking_id}|${inventory_item_id}`
  if (returnIds.length > 0) {
    const photos = await fetchAllPagesByChunks<any, string>(
      returnIds,
      (ids, from, to) =>
        supabase
          .from('booking_return_photos')
          .select('id, booking_return_id, booking_item_return_id')
          .in('booking_return_id', ids)
          .is('replaced_at', null)
          .order('id', { ascending: true })
          .range(from, to)
    );

    // Bucket by return first: scanning the whole photo list once per queue row
    // was quadratic, which the row cap used to hide.
    const photosByReturn = new Map<string, any[]>();
    for (const photo of photos) {
      const bucket = photosByReturn.get(photo.booking_return_id);
      if (bucket) bucket.push(photo);
      else photosByReturn.set(photo.booking_return_id, [photo]);
    }

    for (const r of rawRows as any[]) {
      const br = returnsByBooking.get(r.booking_id);
      if (!br) continue;
      const ir = itemReturnByKey.get(`${r.booking_id}|${r.inventory_item_id}`);
      const count = (photosByReturn.get(br.id) || []).filter((p: any) =>
        // Booking-level photo (no item return), or this item's photo
        p.booking_item_return_id === null || p.booking_item_return_id === ir?.id
      ).length;
      photoCountByKey.set(`${r.booking_id}|${r.inventory_item_id}`, count);
    }
  }

  // Step 5: next booking per inventory_item
  const nextBookingByItem = new Map<string, {
    id: string; start_date: string; end_date: string; customer_name: string;
  }>();
  if (inventoryItemIds.length > 0) {
    const nowIso = new Date().toISOString();
    const nextBookings = await fetchAllPagesByChunks<any, string>(
      inventoryItemIds,
      (ids, from, to) =>
        sb
          .from('booking_items')
          .select(
            `id,
         inventory_item_id,
         bookings!inner ( id, start_date, end_date, customer_firstname, customer_lastname, booking_state )`
          )
          .in('inventory_item_id', ids)
          .gte('bookings.start_date', nowIso)
          .in('bookings.booking_state', ['confirmed', 'open', 'active'])
          .order('id', { ascending: true })
          .range(from, to)
    );

    // Reduce to earliest per inventory_item_id
    for (const row of nextBookings as any[]) {
      const itemId = row.inventory_item_id;
      const b = Array.isArray(row.bookings) ? row.bookings[0] : row.bookings;
      if (!b) continue;
      const existing = nextBookingByItem.get(itemId);
      if (!existing || new Date(b.start_date) < new Date(existing.start_date)) {
        nextBookingByItem.set(itemId, {
          id: b.id,
          start_date: b.start_date,
          end_date: b.end_date,
          customer_name: `${b.customer_firstname || ''} ${b.customer_lastname || ''}`.trim(),
        });
      }
    }
  }

  // Step 6: issues for linked issue_ids (kept for back-compat)
  const issuesById = new Map<string, { title: string; severity: string }>();
  if (issueIds.length > 0) {
    const issuesData = await fetchAllPagesByChunks<any, string>(
      issueIds,
      (ids, from, to) =>
        supabase
          .from('issues')
          .select('id, title, severity')
          .in('id', ids)
          .order('id', { ascending: true })
          .range(from, to)
    );
    for (const i of issuesData) issuesById.set(i.id, { title: i.title, severity: i.severity });
  }

  // Step 6b: ALL open issues on the inventory_items in the result, regardless
  // of source (internal, customer_return, system, equipment-check linkage).
  // Lets the queue surface pre-existing problems on the bike.
  const OPEN_ISSUE_STATUSES = ['reported', 'confirmed', 'repair_assigned'];
  const openIssuesByItem = new Map<string, Array<{ id: string; title: string; severity: string; status: string }>>();
  if (inventoryItemIds.length > 0) {
    const openIssuesData = await fetchAllPagesByChunks<any, string>(
      inventoryItemIds,
      (ids, from, to) =>
        supabase
          .from('issues')
          .select('id, inventory_item_id, title, severity, status')
          .in('inventory_item_id', ids)
          .in('status', OPEN_ISSUE_STATUSES)
          .order('id', { ascending: true })
          .range(from, to)
    );
    for (const i of openIssuesData as any[]) {
      const key = i.inventory_item_id;
      if (!openIssuesByItem.has(key)) openIssuesByItem.set(key, []);
      openIssuesByItem.get(key)!.push({
        id: i.id, title: i.title, severity: i.severity, status: i.status,
      });
    }
  }

  // Step 6c: translated product_type names for the current language
  const productTypeIds: string[] = Array.from(new Set(
    (rawRows as any[])
      .map((r) => {
        const item = Array.isArray(r.inventory_items) ? r.inventory_items[0] : r.inventory_items;
        return item?.product_type_id as string | null;
      })
      .filter((id): id is string => Boolean(id))
  ));
  const productNameTranslations = await fetchProductTypeNameTranslations(
    productTypeIds, args.language, args.defaultLanguage,
  );

  // Step 7: assemble
  const out: EquipmentCheckListItem[] = (rawRows as any[]).map((r) => {
    const booking = Array.isArray(r.bookings) ? r.bookings[0] : r.bookings;
    const item = Array.isArray(r.inventory_items) ? r.inventory_items[0] : r.inventory_items;
    const productTypes = item?.product_types
      ? Array.isArray(item.product_types)
        ? item.product_types[0]
        : item.product_types
      : null;

    const br = returnsByBooking.get(r.booking_id);
    const ir = itemReturnByKey.get(`${r.booking_id}|${r.inventory_item_id}`);

    let customer_return: CustomerReturnBadge = { kind: 'none' };
    if (ir) {
      customer_return =
        ir.item_status === 'ok' ? { kind: 'item_ok' } :
        ir.item_status === 'issue' ? { kind: 'item_issue' } :
        ir.item_status === 'damaged' ? { kind: 'item_damaged' } :
        ir.item_status === 'missing' ? { kind: 'item_missing' } :
        { kind: 'none' };
    } else if (br) {
      customer_return = br.overall_status === 'all_ok' ? { kind: 'booking_ok' } : { kind: 'booking_issues' };
    }

    // Flagged after check: row was marked checked, then a customer signal
    // pointing to a problem arrived after that timestamp. Compare checked_at
    // with the booking_returns.created_at of the (latest) report.
    let customer_flagged_after_check = false;
    if (r.status === 'checked' && r.checked_at && br?.created_at) {
      const customerFlaggedThisItem =
        ir && ir.item_status !== 'ok' && ir.item_status !== null;
      const customerFlaggedBooking =
        br.overall_status === 'issues_reported' && !ir;
      if (customerFlaggedThisItem || customerFlaggedBooking) {
        if (new Date(br.created_at) > new Date(r.checked_at)) {
          customer_flagged_after_check = true;
        }
      }
    }

    const next = nextBookingByItem.get(r.inventory_item_id);
    const issue = r.issue_id ? issuesById.get(r.issue_id) : null;

    return {
      id: r.id,
      merchant_id: r.merchant_id,
      location_id: r.location_id,
      booking_id: r.booking_id,
      inventory_item_id: r.inventory_item_id,
      status: r.status,
      checked_by: r.checked_by,
      checked_by_name: ((Array.isArray(r.checked_by_profile) ? r.checked_by_profile[0] : r.checked_by_profile) as any)?.full_name ?? null,
      checked_at: r.checked_at,
      note: r.note,
      photo_paths: r.photo_paths || [],
      issue_id: r.issue_id,
      obsolete_at: r.obsolete_at,
      obsolete_reason: r.obsolete_reason,
      created_at: r.created_at,
      updated_at: r.updated_at,
      order_id: booking?.order_id ?? '',
      customer_name: `${booking?.customer_firstname || ''} ${booking?.customer_lastname || ''}`.trim(),
      booking_start_date: booking?.start_date ?? '',
      booking_end_date: booking?.end_date ?? '',
      booking_returned_at: booking?.returned_at ?? null,
      booking_state: booking?.booking_state ?? '',
      item_number: item?.item_number ?? '',
      combination_code: item?.combination_code ?? null,
      product_type_id: item?.product_type_id ?? null,
      product_name:
        (item?.product_type_id && productNameTranslations.get(item.product_type_id)) ||
        productTypes?.product_name ||
        null,
      customer_return,
      customer_return_photos: photoCountByKey.get(`${r.booking_id}|${r.inventory_item_id}`) || 0,
      next_booking_id: next?.id ?? null,
      next_booking_start: next?.start_date ?? null,
      next_booking_end: next?.end_date ?? null,
      next_booking_customer: next?.customer_name ?? null,
      open_issues: openIssuesByItem.get(r.inventory_item_id) || [],
      issue_title: issue?.title ?? null,
      issue_severity: issue?.severity ?? null,
      customer_flagged_after_check,
    };
  });

  return { data: out, has_more_completed, error: null };
}

// ---------------------------------------------------------------------------
// Photo upload
// ---------------------------------------------------------------------------

const PHOTOS_BUCKET = 'equipment-check-photos';

export async function uploadEquipmentCheckPhoto(args: {
  file: File;
  merchantId: string;
  locationId: string;
  equipmentCheckId: string;
}): Promise<{ path: string | null; error: string | null }> {
  const compressed = await compressImage(args.file);
  const ext = compressed.type.split('/')[1] || 'jpg';
  const filename = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const path = `${args.merchantId}/${args.locationId}/${args.equipmentCheckId}/${filename}`;

  const { error } = await supabase.storage.from(PHOTOS_BUCKET).upload(path, compressed, {
    contentType: compressed.type,
    upsert: false,
  });
  if (error) return { path: null, error: error.message };
  return { path, error: null };
}

export function getEquipmentCheckPhotoUrl(path: string): string {
  return supabase.storage.from(PHOTOS_BUCKET).getPublicUrl(path).data.publicUrl;
}

// Bucket is private — use signed URLs for read access. Returns a parallel
// array of signed URLs (same order as paths); null entries indicate failed
// signing for a path.
export async function getEquipmentCheckPhotoSignedUrls(
  paths: string[],
  expiresIn: number = 3600,
): Promise<Array<{ path: string; url: string | null }>> {
  if (paths.length === 0) return [];
  const { data, error } = await supabase.storage
    .from(PHOTOS_BUCKET)
    .createSignedUrls(paths, expiresIn);
  if (error || !data) return paths.map((p) => ({ path: p, url: null }));
  // Preserve input order
  const byPath = new Map<string, string | null>();
  for (const d of data) byPath.set(d.path || '', d.signedUrl || null);
  return paths.map((p) => ({ path: p, url: byPath.get(p) ?? null }));
}

// ---------------------------------------------------------------------------
// Mark checked
// ---------------------------------------------------------------------------

export async function markEquipmentChecked(args: {
  id: string;
  merchantId: string;
  locationId: string;
  userId: string;
  note: string | null;
  photos: File[];
}): Promise<{ ok: boolean; error: string | null }> {
  const uploadedPaths: string[] = [];
  for (const file of args.photos) {
    const { path, error } = await uploadEquipmentCheckPhoto({
      file,
      merchantId: args.merchantId,
      locationId: args.locationId,
      equipmentCheckId: args.id,
    });
    if (error || !path) {
      return { ok: false, error: error || 'photo_upload_failed' };
    }
    uploadedPaths.push(path);
  }

  const { error } = await sb
    .from('equipment_checks')
    .update({
      status: 'checked',
      checked_by: args.userId,
      checked_at: new Date().toISOString(),
      note: args.note,
      photo_paths: uploadedPaths,
    })
    .eq('id', args.id);

  if (error) return { ok: false, error: error.message };
  return { ok: true, error: null };
}

// ---------------------------------------------------------------------------
// Link an issue to a check (check stays pending)
// ---------------------------------------------------------------------------

export async function setEquipmentCheckIssue(args: {
  id: string;
  issueId: string;
}): Promise<{ ok: boolean; error: string | null }> {
  const { error } = await sb
    .from('equipment_checks')
    .update({ issue_id: args.issueId })
    .eq('id', args.id);
  if (error) return { ok: false, error: error.message };
  return { ok: true, error: null };
}

// ---------------------------------------------------------------------------
// Swap candidates (inventory items eligible to replace the current one
// on the next booking). Mirrors the DB swap function's filters so the user
// sees ineligible items marked rather than getting a backend rejection.
// ---------------------------------------------------------------------------

export interface SwapCandidate {
  id: string;
  item_number: string;
  combination_code: string | null;
  size: string | null;
  active: boolean;
  status: 'available' | 'blocked' | 'conflict' | 'inactive';
  conflict_summary?: string;
}

export async function getSwapCandidates(args: {
  currentInventoryItemId: string;
  productTypeId: string;
  locationId: string;
  bookingId: string;
  windowStart: string;
  windowEnd: string;
}): Promise<{ data: SwapCandidate[]; error: string | null }> {
  // Same-product-type candidates at this location, excluding the current item.
  const { data: items, error } = await supabase
    .from('inventory_items')
    .select('id, item_number, combination_code, size, active, product_type_id')
    .eq('product_type_id', args.productTypeId)
    .neq('id', args.currentInventoryItemId)
    .order('item_number', { ascending: true });
  if (error) return { data: [], error: error.message };
  if (!items || items.length === 0) return { data: [], error: null };

  const candidateIds = items.map((i: any) => i.id);

  // Blocking-time overlaps
  const { data: blocks } = await supabase
    .from('inventory_blocking_times')
    .select('inventory_item_id, blocked_from, blocked_until, blocking_reason')
    .in('inventory_item_id', candidateIds)
    .lt('blocked_from', args.windowEnd)
    .gt('blocked_until', args.windowStart);

  const blockedSet = new Map<string, string>();
  for (const b of (blocks || []) as any[]) {
    blockedSet.set(b.inventory_item_id, b.blocking_reason || 'Blocked');
  }

  // Conflicting bookings on the window
  const { data: conflicts } = await supabase
    .from('booking_items')
    .select(
      `inventory_item_id,
       bookings!inner ( id, order_id, customer_firstname, customer_lastname, start_date, end_date, booking_state )`
    )
    .in('inventory_item_id', candidateIds)
    .neq('booking_id', args.bookingId)
    .in('bookings.booking_state', ['confirmed', 'open', 'active', 'completed', 'returned'])
    .lt('bookings.start_date', args.windowEnd)
    .gt('bookings.end_date', args.windowStart);

  const conflictSet = new Map<string, string>();
  for (const c of (conflicts || []) as any[]) {
    const b = Array.isArray(c.bookings) ? c.bookings[0] : c.bookings;
    if (!b) continue;
    const name = `${b.customer_firstname || ''} ${b.customer_lastname || ''}`.trim() || b.order_id || 'another booking';
    if (!conflictSet.has(c.inventory_item_id)) {
      conflictSet.set(c.inventory_item_id, `Conflicts with ${name}`);
    }
  }

  const out: SwapCandidate[] = items.map((i: any) => {
    let status: SwapCandidate['status'] = 'available';
    let conflict_summary: string | undefined;
    if (!i.active) {
      status = 'inactive';
      conflict_summary = 'Inactive';
    } else if (blockedSet.has(i.id)) {
      status = 'blocked';
      conflict_summary = blockedSet.get(i.id);
    } else if (conflictSet.has(i.id)) {
      status = 'conflict';
      conflict_summary = conflictSet.get(i.id);
    }
    return {
      id: i.id,
      item_number: i.item_number,
      combination_code: i.combination_code,
      size: i.size,
      active: i.active,
      status,
      conflict_summary,
    };
  });

  // Available first, then blocked / conflict / inactive
  out.sort((a, b) => {
    const order: Record<SwapCandidate['status'], number> = {
      available: 0, blocked: 1, conflict: 2, inactive: 3,
    };
    return order[a.status] - order[b.status];
  });

  return { data: out, error: null };
}

// ---------------------------------------------------------------------------
// Swap booking item (calls SECURITY DEFINER RPC)
// ---------------------------------------------------------------------------

export async function swapBookingItemEquipment(args: {
  bookingId: string;
  oldInventoryItemId: string;
  newInventoryItemId: string;
}): Promise<{ ok: boolean; error: string | null }> {
  const { data, error } = await sb.rpc('swap_booking_item_equipment', {
    p_booking_id: args.bookingId,
    p_old_inventory_item: args.oldInventoryItemId,
    p_new_inventory_item: args.newInventoryItemId,
  });
  if (error) return { ok: false, error: error.message };
  if (data && typeof data === 'object' && (data as any).ok === true) return { ok: true, error: null };
  return { ok: false, error: ((data as any)?.error as string) || 'swap_failed' };
}

// ---------------------------------------------------------------------------
// Ensure equipment_checks exist for overdue bookings (no return report)
// ---------------------------------------------------------------------------

export async function ensureEquipmentChecksForOverdue(args: {
  merchantId: string;
  locationId: string | null;
}): Promise<{ created: number; error: string | null }> {
  const { data, error } = await sb.rpc('ensure_equipment_checks_for_overdue_bookings', {
    p_merchant_id: args.merchantId,
    p_location_id: args.locationId,
  });
  if (error) return { created: 0, error: error.message };
  return { created: (data as number) || 0, error: null };
}

// Marks pending equipment_checks rows obsolete when the next rental of the
// same inventory item has already started (the bike is out again, so the
// previous check is moot until it returns).
export async function markObsoleteForStartedRentals(args: {
  merchantId: string;
  locationId: string | null;
}): Promise<{ updated: number; error: string | null }> {
  const { data, error } = await sb.rpc('mark_pending_checks_obsolete_for_started_rentals', {
    p_merchant_id: args.merchantId,
    p_location_id: args.locationId,
  });
  if (error) return { updated: 0, error: error.message };
  return { updated: (data as number) || 0, error: null };
}

// ---------------------------------------------------------------------------
// Upcoming handovers (next 7 days)
// ---------------------------------------------------------------------------

export async function getUpcomingHandovers(args: {
  merchantId: string;
  locationId: string | null;
  language?: string;
  defaultLanguage?: string;
}): Promise<{ data: UpcomingHandover[]; error: string | null }> {
  const now = new Date();
  const horizon = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  let bookingsQ = supabase
    .from('bookings')
    .select(
      `id, order_id, customer_firstname, customer_lastname, start_date, booking_state,
       booking_items (
         id, inventory_item_id, product_type_id, item_name, quantity,
         inventory_items (
           id, item_number, combination_code, product_type_id,
           product_types!inventory_items_product_type_id_fkey ( product_name )
         )
       )`
    )
    .gte('start_date', now.toISOString())
    .lte('start_date', horizon.toISOString())
    .in('booking_state', ['confirmed', 'open', 'active']);

  // Filter to merchant via location.merchant_id (bookings has location_id)
  if (args.locationId) {
    bookingsQ = bookingsQ.eq('location_id', args.locationId);
  } else {
    // Get merchant's locations and filter
    const { data: locs } = await supabase
      .from('locations')
      .select('id')
      .eq('merchant_id', args.merchantId);
    const locIds = (locs || []).map((l: any) => l.id);
    if (locIds.length === 0) return { data: [], error: null };
    bookingsQ = bookingsQ.in('location_id', locIds);
  }

  const { data: bookings, error } = await bookingsQ.order('start_date', { ascending: true });
  if (error) return { data: [], error: error.message };
  if (!bookings || bookings.length === 0) return { data: [], error: null };

  // Tracked items: those with an inventory_item_id. We need:
  //   - last check status (ready vs pending)
  //   - open issues (severity → attention dot)
  //   - currently-active blocking-time (forces red dot)
  const trackedItemIds = new Set<string>();
  for (const b of bookings as any[]) {
    for (const bi of b.booking_items || []) {
      if (bi.inventory_item_id) trackedItemIds.add(bi.inventory_item_id);
    }
  }

  const lastStatusByItem = new Map<string, 'checked' | 'pending' | 'none'>();
  const maxSeverityByItem = new Map<string, 'critical' | 'high' | 'medium' | 'low'>();
  const blockedItems = new Set<string>();
  // Bike currently out on an active rental → maps item_id → that rental's end_date.
  const currentlyOutByItem = new Map<string, { end_date: string; start_date: string }>();

  if (trackedItemIds.size > 0) {
    const ids = Array.from(trackedItemIds);

    const [{ data: lastChecks }, { data: openIssues }, { data: blocks }, { data: currentlyOut }] = await Promise.all([
      sb
        .from('equipment_checks')
        .select('inventory_item_id, status, updated_at')
        .in('inventory_item_id', ids)
        .order('updated_at', { ascending: false }),
      supabase
        .from('issues')
        .select('inventory_item_id, severity, status')
        .in('inventory_item_id', ids)
        .in('status', ['reported', 'confirmed', 'repair_assigned']),
      supabase
        .from('inventory_blocking_times')
        .select('inventory_item_id, blocked_from, blocked_until')
        .in('inventory_item_id', ids)
        .lte('blocked_from', now.toISOString())
        .gte('blocked_until', now.toISOString()),
      supabase
        .from('booking_items')
        .select('inventory_item_id, bookings!inner ( start_date, end_date, booking_state )')
        .in('inventory_item_id', ids)
        .eq('bookings.booking_state', 'active')
        .lte('bookings.start_date', now.toISOString()),
    ]);

    for (const c of (lastChecks || []) as any[]) {
      if (!lastStatusByItem.has(c.inventory_item_id)) {
        if (c.status === 'checked') lastStatusByItem.set(c.inventory_item_id, 'checked');
        else if (c.status === 'pending') lastStatusByItem.set(c.inventory_item_id, 'pending');
        else lastStatusByItem.set(c.inventory_item_id, 'none');
      }
    }

    const rank: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
    for (const i of (openIssues || []) as any[]) {
      const cur = maxSeverityByItem.get(i.inventory_item_id);
      if (!cur || (rank[i.severity] || 0) > (rank[cur] || 0)) {
        maxSeverityByItem.set(i.inventory_item_id, i.severity);
      }
    }

    for (const b of (blocks || []) as any[]) {
      blockedItems.add(b.inventory_item_id);
    }

    // Pick the latest-started active rental per item (rare case of overlaps).
    for (const row of (currentlyOut || []) as any[]) {
      const b = Array.isArray(row.bookings) ? row.bookings[0] : row.bookings;
      if (!b) continue;
      const existing = currentlyOutByItem.get(row.inventory_item_id);
      if (!existing || new Date(b.start_date) > new Date(existing.start_date)) {
        currentlyOutByItem.set(row.inventory_item_id, {
          start_date: b.start_date,
          end_date: b.end_date,
        });
      }
    }
  }

  // Translated product_type names for the current language. Collect IDs from
  // BOTH tracked items (inventory_items.product_type_id) AND add-on items
  // (booking_items.product_type_id) so the add-on labels get translated too.
  const productTypeIdsForHandovers: string[] = Array.from(new Set(
    (bookings as any[]).flatMap((b) =>
      (b.booking_items || []).flatMap((bi: any) => {
        const ii = Array.isArray(bi.inventory_items) ? bi.inventory_items[0] : bi.inventory_items;
        return [ii?.product_type_id, bi.product_type_id] as Array<string | null>;
      }),
    ).filter((id: string | null): id is string => Boolean(id))
  ));
  const handoverProductNameTranslations = await fetchProductTypeNameTranslations(
    productTypeIdsForHandovers, args.language, args.defaultLanguage,
  );

  const out: UpcomingHandover[] = (bookings as any[]).map((b) => {
    const tracked: UpcomingHandoverTrackedItem[] = [];
    const addons: UpcomingHandoverAddon[] = [];

    for (const bi of (b.booking_items || []) as any[]) {
      if (bi.inventory_item_id) {
        const ii = Array.isArray(bi.inventory_items) ? bi.inventory_items[0] : bi.inventory_items;
        const pt = ii?.product_types
          ? Array.isArray(ii.product_types) ? ii.product_types[0] : ii.product_types
          : null;
        const sev = maxSeverityByItem.get(bi.inventory_item_id);
        const blocked = blockedItems.has(bi.inventory_item_id);
        // Red: critical/high severity OR currently blocked. Yellow: medium/low.
        let attention: 'red' | 'yellow' | null = null;
        let attention_reason: string | null = null;
        if (blocked) {
          attention = 'red';
          attention_reason = 'blocked';
        } else if (sev === 'critical' || sev === 'high') {
          attention = 'red';
          attention_reason = `open ${sev} issue`;
        } else if (sev === 'medium' || sev === 'low') {
          attention = 'yellow';
          attention_reason = `open ${sev} issue`;
        }
        // "Currently out" trumps any stale check status: a bike that's
        // physically out on another active rental cannot be "Checked & ready".
        const currentOut = currentlyOutByItem.get(bi.inventory_item_id);
        const last_check_status: UpcomingHandoverTrackedItem['last_check_status'] =
          currentOut
            ? 'currently_out'
            : (lastStatusByItem.get(bi.inventory_item_id) || 'none');
        tracked.push({
          inventory_item_id: bi.inventory_item_id,
          item_number: ii?.item_number ?? '',
          combination_code: ii?.combination_code ?? null,
          product_name:
            (ii?.product_type_id && handoverProductNameTranslations.get(ii.product_type_id)) ||
            pt?.product_name ||
            null,
          last_check_status,
          currently_out_end_date: currentOut?.end_date ?? null,
          attention,
          attention_reason,
        });
      } else {
        addons.push({
          booking_item_id: bi.id,
          item_name:
            (bi.product_type_id && handoverProductNameTranslations.get(bi.product_type_id)) ||
            bi.item_name ||
            '—',
          quantity: bi.quantity ?? 1,
        });
      }
    }

    tracked.sort((a, x) => a.item_number.localeCompare(x.item_number));
    addons.sort((a, x) => a.item_name.localeCompare(x.item_name));

    return {
      booking_id: b.id,
      order_id: b.order_id,
      customer_name: `${b.customer_firstname || ''} ${b.customer_lastname || ''}`.trim(),
      start_date: b.start_date,
      tracked,
      addons,
    };
  });

  return { data: out, error: null };
}

// ---------------------------------------------------------------------------
// Bikes tab — list every active inventory item at the location, with the
// info this role needs: code, open issues, current blocking status, recent
// check history. Universe-of-bikes search target.
// ---------------------------------------------------------------------------

export interface BikeListItem {
  inventory_item_id: string;
  item_number: string;
  combination_code: string | null;
  size: string | null;
  product_type_id: string | null;
  product_name: string | null;
  last_maintenance_date: string | null;
  // Aggregates
  open_issues_count: number;
  open_issues_max_severity: 'low' | 'medium' | 'high' | 'critical' | null;
  currently_blocked_until: string | null; // null if not blocked
  blocking_reason: string | null;
  has_pending_check: boolean;
  pending_check_id: string | null;
}

export async function getInventoryItemsAtLocation(args: {
  merchantId: string;
  locationId: string;
  language?: string;
  defaultLanguage?: string;
}): Promise<{ data: BikeListItem[]; error: string | null }> {
  // 1. inventory_items + product_types, scoped to the location IN THE QUERY.
  //
  // This used to fetch every active item the caller could see and filter by
  // location in JS. That is not just wasteful: the fetch stopped at the row cap
  // first, so once a merchant passed 1000 active items the surviving page might
  // contain none from the requested location and the screen came back empty —
  // a truncation that reads as "no bikes here". Filtering through the `!inner`
  // join makes the cap irrelevant to correctness, and the fetch is paged so the
  // location's own fleet is never truncated either.
  let scoped: any[];
  try {
    scoped = dedupeById(
      await fetchAllPages<any>((from, to) =>
        supabase
          .from('inventory_items')
          .select(
            `id, item_number, combination_code, size, active, last_maintenance_date, product_type_id,
       product_types!inventory_items_product_type_id_fkey!inner ( product_name, location_id )`
          )
          .eq('active', true)
          .eq('product_types.location_id', args.locationId)
          .order('item_number', { ascending: true })
          .order('id', { ascending: true })
          .range(from, to)
      )
    );
  } catch (error: any) {
    return { data: [], error: error?.message || String(error) };
  }

  const ids = scoped.map((i) => i.id as string);
  if (ids.length === 0) return { data: [], error: null };

  // 2. Open issues
  const OPEN = ['reported', 'confirmed', 'repair_assigned'];
  const issuesData = await fetchAllPagesByChunks<any, string>(
    ids,
    (chunkIds, from, to) =>
      supabase
        .from('issues')
        .select('id, inventory_item_id, severity, status')
        .in('inventory_item_id', chunkIds)
        .in('status', OPEN)
        .order('id', { ascending: true })
        .range(from, to)
  );

  const severityRank: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
  const issuesByItem = new Map<string, { count: number; max: BikeListItem['open_issues_max_severity'] }>();
  for (const i of issuesData as any[]) {
    const e = issuesByItem.get(i.inventory_item_id) || { count: 0, max: null };
    e.count += 1;
    if (!e.max || (severityRank[i.severity] || 0) > (severityRank[e.max] || 0)) {
      e.max = i.severity;
    }
    issuesByItem.set(i.inventory_item_id, e);
  }

  // 3. Currently active blocking-times (blocked_from <= now <= blocked_until)
  const nowIso = new Date().toISOString();
  const blocksData = await fetchAllPagesByChunks<any, string>(
    ids,
    (chunkIds, from, to) =>
      supabase
        .from('inventory_blocking_times')
        .select('id, inventory_item_id, blocked_from, blocked_until, blocking_reason')
        .in('inventory_item_id', chunkIds)
        .lte('blocked_from', nowIso)
        .gte('blocked_until', nowIso)
        .order('id', { ascending: true })
        .range(from, to)
  );

  const blockByItem = new Map<string, { until: string; reason: string | null }>();
  for (const b of blocksData as any[]) {
    // Keep the latest-ending block if there are multiple
    const existing = blockByItem.get(b.inventory_item_id);
    if (!existing || new Date(b.blocked_until) > new Date(existing.until)) {
      blockByItem.set(b.inventory_item_id, { until: b.blocked_until, reason: b.blocking_reason });
    }
  }

  // 4. Pending equipment_checks (one per item if any)
  const pendingData = await fetchAllPagesByChunks<any, string>(
    ids,
    (chunkIds, from, to) =>
      sb
        .from('equipment_checks')
        .select('id, inventory_item_id, created_at')
        .in('inventory_item_id', chunkIds)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to)
  );

  const pendingByItem = new Map<string, string>(); // item_id → check_id
  for (const c of pendingData as any[]) {
    if (!pendingByItem.has(c.inventory_item_id)) {
      pendingByItem.set(c.inventory_item_id, c.id);
    }
  }

  // Translated product_type names for the current language
  const bikeProductTypeIds: string[] = Array.from(new Set(
    scoped.map((i) => i.product_type_id as string | null).filter((id): id is string => Boolean(id))
  ));
  const bikeProductNameTranslations = await fetchProductTypeNameTranslations(
    bikeProductTypeIds, args.language, args.defaultLanguage,
  );

  const out: BikeListItem[] = scoped.map((i) => {
    const pt = Array.isArray(i.product_types) ? i.product_types[0] : i.product_types;
    const issues = issuesByItem.get(i.id);
    const block = blockByItem.get(i.id);
    return {
      inventory_item_id: i.id,
      item_number: i.item_number,
      combination_code: i.combination_code,
      size: i.size,
      product_type_id: i.product_type_id,
      product_name:
        (i.product_type_id && bikeProductNameTranslations.get(i.product_type_id)) ||
        pt?.product_name ||
        null,
      last_maintenance_date: i.last_maintenance_date ?? null,
      open_issues_count: issues?.count ?? 0,
      open_issues_max_severity: issues?.max ?? null,
      currently_blocked_until: block?.until ?? null,
      blocking_reason: block?.reason ?? null,
      has_pending_check: pendingByItem.has(i.id),
      pending_check_id: pendingByItem.get(i.id) ?? null,
    };
  });

  return { data: out, error: null };
}

export interface BikeDetail {
  recent_checks: Array<{
    id: string;
    status: 'pending' | 'checked' | 'obsolete';
    checked_at: string | null;
    checked_by_name: string | null;
    created_at: string;
    booking_id: string;
    order_id: string | null;
    customer_name: string;
    note: string | null;
    photo_paths: string[];
  }>;
  open_issues: Array<{ id: string; title: string; severity: string; status: string }>;
}

export async function getBikeDetail(args: { inventoryItemId: string }): Promise<{ data: BikeDetail; error: string | null }> {
  const { data: recent } = await sb
    .from('equipment_checks')
    .select(
      `id, status, checked_at, created_at, booking_id, note, photo_paths,
       bookings ( order_id, customer_firstname, customer_lastname ),
       checked_by_profile:profiles!equipment_checks_checked_by_fkey ( full_name )`
    )
    .eq('inventory_item_id', args.inventoryItemId)
    .order('created_at', { ascending: false })
    .limit(8);

  const OPEN = ['reported', 'confirmed', 'repair_assigned'];
  const { data: openIssues } = await supabase
    .from('issues')
    .select('id, title, severity, status')
    .eq('inventory_item_id', args.inventoryItemId)
    .in('status', OPEN);

  return {
    data: {
      recent_checks: ((recent || []) as any[]).map((r) => {
        const b = Array.isArray(r.bookings) ? r.bookings[0] : r.bookings;
        const cb = Array.isArray(r.checked_by_profile) ? r.checked_by_profile[0] : r.checked_by_profile;
        return {
          id: r.id,
          status: r.status,
          checked_at: r.checked_at,
          checked_by_name: cb?.full_name ?? null,
          created_at: r.created_at,
          booking_id: r.booking_id,
          order_id: b?.order_id ?? null,
          customer_name: `${b?.customer_firstname || ''} ${b?.customer_lastname || ''}`.trim(),
          note: r.note,
          photo_paths: r.photo_paths || [],
        };
      }),
      open_issues: ((openIssues || []) as any[]).map((i) => ({
        id: i.id, title: i.title, severity: i.severity, status: i.status,
      })),
    },
    error: null,
  };
}

// ---------------------------------------------------------------------------
// Priority computation (client-side)
// ---------------------------------------------------------------------------

export type PriorityTier = 'P1' | 'P2' | 'P3' | 'P4' | 'P5' | 'P6';

export function computePriority(check: EquipmentCheckListItem, now: Date = new Date()): PriorityTier {
  const next = check.next_booking_start ? new Date(check.next_booking_start) : null;
  const isToday = next ? isSameCalendarDay(next, now) : false;
  const isTomorrow = next ? isSameCalendarDay(next, addDays(now, 1)) : false;

  const flaggedByCustomer =
    check.customer_return.kind === 'item_issue' ||
    check.customer_return.kind === 'item_damaged' ||
    check.customer_return.kind === 'item_missing';

  const overdueNoReport =
    check.customer_return.kind === 'none' &&
    check.booking_state !== 'returned' &&
    !check.booking_returned_at &&
    !!check.booking_end_date &&
    new Date(check.booking_end_date) < now;

  if (isToday) return 'P1';
  if (isTomorrow) return 'P2';
  if (flaggedByCustomer) return 'P3';
  if (overdueNoReport) return 'P4';
  if (next) return 'P5';
  return 'P6';
}

function isSameCalendarDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function addDays(d: Date, days: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + days);
  return r;
}
